// frontend/src/components/common/ImportResult.jsx
//
// The one renderer for the unified import envelope (api.services.table_import).
//
// Every import answers with the same {summary, errors, info,
// unknown_identifiers, cleared} shape.
//
// Envelope blocks are universal and live here. What each ENTITY calls its own
// per-kind counters is not, and arrives as `counters` — pairs of a summary key
// and the label to show when it is greater than zero.

import { Alert, Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import StatusChip from './StatusChip';
import { flattenApiError } from '../../utils/parseApiError';

const MAX_INLINE = 10;

/**
 * @param {Object}   props
 * @param {Object}   props.result   the import envelope
 * @param {Array}    [props.counters] [{ key, label }] per-kind lines to show
 *   under the headline when `summary[key] > 0`. Entity vocabulary — "items
 *   added", "rows merged" — which is why it isn't hardcoded here.
 * @param {Function} [props.onDownloadErrors]
 */
export default function ImportResult({ result, counters = [], onDownloadErrors }) {
  const { t } = useTranslation();

  const summary = result?.summary;
  if (!summary) return null;

  const errors = result.errors || [];
  const infoArray = result.info || [];
  const clearedArray = result.cleared || [];
  const unknownIdentifiers = result.unknown_identifiers || [];

  const totalRows = summary.total_rows || 0;
  const unknownCount = summary.unknown_count || 0;
  const totalErrors = summary.error_count || 0;
  const appliedCount =
    (summary.created || 0) + (summary.updated || 0) + (summary.kept || 0);

  const fileLevelInfo = infoArray.filter((e) => e.row == null);
  const rowLevelInfo = infoArray.filter((e) => e.row != null);
  const shownCounters = counters.filter(({ key }) => (summary[key] || 0) > 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Unconditional. Every other block is conditional, so without this one
          a file whose rows were ALL rejected — the single most important thing
          an import can report — renders as an empty box. */}
      <Alert
        severity={appliedCount > 0 ? 'success' : 'warning'}
        variant="outlined"
      >
        {t('bulkImport.resultHeadline', { applied: appliedCount, total: totalRows })}
        {shownCounters.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.25, pl: 2 }}>
            {shownCounters.map(({ key, label }) => (
              <Typography key={key} component="li" variant="body2">
                {label(summary[key])}
              </Typography>
            ))}
          </Box>
        )}
      </Alert>

      {/* Rows that named nothing in the catalogue. */}
      {unknownCount > 0 && (
        <Alert severity="warning" variant="outlined">
          <Typography variant="body2">
            {t('bulkImport.resultUnknown', { count: unknownCount })}
          </Typography>
          {unknownIdentifiers.length > 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {unknownIdentifiers.slice(0, MAX_INLINE).join(', ')}
              {unknownIdentifiers.length > MAX_INLINE && '…'}
            </Typography>
          )}
        </Alert>
      )}

      {/* What a blank-policy clear emptied, named. Authorised before the
          write by the checkbox; this is the receipt after it. */}
      {clearedArray.length > 0 && (
        <Alert severity="warning" variant="outlined">
          {clearedArray.map((entry) => (
            <Typography key={entry.field} variant="body2">
              {t('bulkImport.resultCleared', { field: entry.field, count: entry.count })}
              {entry.identifiers?.length > 0 && (
                <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                  {' — '}
                  {entry.identifiers.slice(0, MAX_INLINE).join(', ')}
                  {entry.identifiers.length > MAX_INLINE && '…'}
                </Typography>
              )}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Info — neutral, never red. File-level messages first as plain text,
          row-level ones as a scrollable list below. */}
      {infoArray.length > 0 && (
        <Alert severity="info" variant="outlined">
          {fileLevelInfo.length > 0 && (
            <Box sx={{ mb: rowLevelInfo.length > 0 ? 1 : 0 }}>
              {fileLevelInfo.map((entry, idx) => (
                <Typography key={`file-${idx}`} variant="body2">
                  {entry.message}
                </Typography>
              ))}
            </Box>
          )}
          {rowLevelInfo.length > 0 && (
            <Box
              sx={{
                maxHeight: 180,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
              }}
            >
              {rowLevelInfo.slice(0, MAX_INLINE).map((entry, idx) => (
                <Box
                  key={`row-${entry.row}-${idx}`}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'baseline',
                    px: 0.5,
                    py: 0.25,
                  }}
                >
                  <StatusChip label={t('bulkImport.errorRow', { row: entry.row })} color="info" />
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    {entry.message}
                  </Typography>
                </Box>
              ))}
              {rowLevelInfo.length > MAX_INLINE && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.75rem',
                    fontStyle: 'italic',
                    color: 'text.secondary',
                    mt: 0.5,
                  }}
                >
                  {t('bulkImport.moreInfo', { count: rowLevelInfo.length - MAX_INLINE })}
                </Typography>
              )}
            </Box>
          )}
        </Alert>
      )}

      {totalErrors > 0 && (
        <>
          <Alert
            severity="warning"
            variant="outlined"
            action={
              totalErrors > MAX_INLINE && onDownloadErrors ? (
                <Button color="inherit" size="small" onClick={onDownloadErrors}>
                  {t('bulkImport.downloadErrors')}
                </Button>
              ) : undefined
            }
          >
            {t('bulkImport.resultErrors', { count: totalErrors })}
          </Alert>
          <Box
            sx={{
              maxHeight: 220,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.default',
            }}
          >
            {errors.slice(0, MAX_INLINE).map((err, idx) => (
              <Box
                key={idx}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderBottom:
                    idx < Math.min(errors.length, MAX_INLINE) - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  display: 'flex',
                  gap: 1,
                  alignItems: 'baseline',
                }}
              >
                <StatusChip
                  label={
                    err.row != null
                      ? t('bulkImport.errorRow', { row: err.row })
                      : (err.identifier || '—')
                  }
                  color="warning"
                />
                <Typography
                  variant="body2"
                  sx={{ fontSize: '0.8rem', color: 'text.secondary' }}
                >
                  {flattenApiError(err.message).join(' · ')}
                </Typography>
              </Box>
            ))}
            {totalErrors > MAX_INLINE && (
              <Box sx={{ px: 1.5, py: 0.75 }}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: '0.8rem', color: 'text.disabled', fontStyle: 'italic' }}
                >
                  {t('bulkImport.moreErrors', { count: totalErrors - MAX_INLINE })}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
